import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/components/ui/button";
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { SafeText } from "@/shared/components/SafeText";
import { sanitizeDisplayText } from "@/shared/lib/safe-text";

export interface RecordOfflineSummary {
  readonly userName: string;
  readonly documentTypeName: string;
  readonly reference: string;
  /** Already trimmed; empty when the operator left it blank. */
  readonly note: string;
}

interface RecordOfflineConfirmationProps {
  readonly summary: RecordOfflineSummary;
  readonly pending: boolean;
  readonly onBack: () => void;
  readonly onConfirm: () => void;
}

const TERM_CLASS = "text-xs font-medium text-muted-foreground";
const VALUE_CLASS = "text-sm text-foreground";

/**
 * Second step of the record dialog (item 37): recording an offline acceptance
 * is a legal act, so the operator reads back who, which document type and
 * which reference before anything is sent. Every value is rendered through
 * the display-text sanitizer (no bidi / invisible characters), the operator's
 * own reference and note included, because a pasted contract id can carry them.
 * Focus moves to the heading when the view appears, so a keyboard or screen
 * reader user is told the step changed instead of landing on nothing.
 */
export function RecordOfflineConfirmation({
  summary,
  pending,
  onBack,
  onConfirm,
}: RecordOfflineConfirmationProps) {
  const { t } = useTranslation();
  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    titleRef.current?.focus();
  }, []);
  const reference = sanitizeDisplayText(summary.reference);
  const note = sanitizeDisplayText(summary.note);
  return (
    <div data-testid="record-offline-confirm-view" className="space-y-4">
      <DialogHeader>
        <DialogTitle
          ref={titleRef}
          tabIndex={-1}
          data-testid="record-offline-confirm-title"
          className="outline-none"
        >
          {t("users.tos.recordDialog.confirm.title")}
        </DialogTitle>
        <DialogDescription>{t("users.tos.recordDialog.confirm.description")}</DialogDescription>
      </DialogHeader>

      <dl className="space-y-3 rounded-md border border-border p-4">
        <div>
          <dt className={TERM_CLASS}>{t("users.tos.recordDialog.confirm.person")}</dt>
          <dd className={VALUE_CLASS}>
            <SafeText testId="record-offline-confirm-person">{summary.userName}</SafeText>
          </dd>
        </div>
        <div>
          <dt className={TERM_CLASS}>{t("users.tos.recordDialog.confirm.documentType")}</dt>
          <dd className={VALUE_CLASS}>
            <SafeText testId="record-offline-confirm-type">{summary.documentTypeName}</SafeText>
          </dd>
        </div>
        <div>
          <dt className={TERM_CLASS}>{t("users.tos.recordDialog.confirm.reference")}</dt>
          <dd className={VALUE_CLASS} data-testid="record-offline-confirm-reference">
            {reference}
          </dd>
        </div>
        <div>
          <dt className={TERM_CLASS}>{t("users.tos.recordDialog.confirm.note")}</dt>
          <dd className={VALUE_CLASS} data-testid="record-offline-confirm-note">
            {note === "" ? (
              <span className="text-muted-foreground">
                {t("users.tos.recordDialog.confirm.noNote")}
              </span>
            ) : (
              note
            )}
          </dd>
        </div>
      </dl>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          data-testid="record-offline-back"
          onClick={onBack}
          disabled={pending}
        >
          {t("users.tos.recordDialog.confirm.back")}
        </Button>
        <Button
          type="button"
          data-testid="record-offline-confirm"
          onClick={onConfirm}
          disabled={pending}
        >
          {pending ? t("users.tos.recordDialog.pending") : t("users.tos.recordDialog.submit")}
        </Button>
      </DialogFooter>
    </div>
  );
}
